import React, { useState, useEffect, useRef } from 'react';
import { last, startCase, toLower } from "lodash";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import formatMetric from "utils/formatMetric";
import chartTrendline from "chartjs-plugin-trendline";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  chartTrendline
);

import jsDig from 'utils/jsDig';
import ChartHelpModal from "dashboards/lite/ChartHelpModal";

export default function LiteLineChart(props) {
  let chevronDown = require("images/lite/icons/chevron-down.png");
  chevronDown = <img className="table-icon chevpdf" src={`/datapulse${chevronDown}`} />;
  let chevronUp = require("images/lite/icons/chevron-down.png");
  chevronUp = <img className="table-icon chevpdf" src={`/datapulse${chevronUp}`} style={{
    transform: 'rotate(180deg)'
  }} />;


  const themeToggleRef = useRef(null);

  // Need to get the theme from the DOM
  const [darkMode, setDarkMode] = useState(document.body.getAttribute('data-theme') === 'dark');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(props.groups && props.groups.length > 0 ? props.groups[0] : null);
  const dropdownRef = useRef(null);
  const [isCollapsed, setIsCollapsed] = useState(props.collapsed || false);
  const collapsible = props.collapsed == true || props.collapsed == false ? true : false;
  const chartContainerRef = useRef(null);
  const spendFunnelAllocationChart = props.metric == "stacked" && props.datasetNames.includes("Top of Funnel")
  const prospectingVsRemarketingChart = props.metric == "stacked" && props.datasetNames.includes("Pros. Spend")
  const brandedVsNonBrandedChart = props.metric == "stacked" && props.datasetNames.includes("Branded")

  useEffect(() => {
    themeToggleRef.current = document.getElementById('theme-toggle');
    const handleThemeChange = () => {
      setDarkMode(themeToggleRef.current.checked);
    };

    if (themeToggleRef.current) {
      themeToggleRef.current.addEventListener('change', handleThemeChange);
    }

    // Check initial state
    if (themeToggleRef.current) {
      setDarkMode(themeToggleRef.current.checked);
    }

    return () => {
        if (themeToggleRef.current) {
          themeToggleRef.current.removeEventListener('change', handleThemeChange);
        }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // Adjust max-height based on the collapsed state and the content of the table
    // const maxHeight = isCollapsed ? "0px" : `${chartContainerRef.current.scrollHeight}px`;
    const maxHeight = isCollapsed ? "0px" : props.fixedHeight ? `${props.fixedHeight}px` : "1700px";
    chartContainerRef.current.style.maxHeight = maxHeight;
  }, [isCollapsed]);

  const toggleCollapse = () => setIsCollapsed(!isCollapsed);
  const toggleDropdown = () => setShowDropdown(!showDropdown);
  const selectGroup = (group) => {
    setSelectedGroup(group);
    setShowDropdown(false);
  };

  const renderGroupSelector = () => {
    if (!props.groups || props.groups.length === 0) {
      return null;
    }

    return (
      <div className="lite-dropdown-toggle-container" ref={dropdownRef}>
        <button className="lite-dropdown-toggle" onClick={toggleDropdown}>
          <span className="title-color-primary">{selectedGroup || 'Select Group'}</span>
          {/* {!showDropdown ? <div className="chevron chevron-down"></div> : <div className="chevron chevron-up"></div>} */}
          {!showDropdown ? chevronDown : chevronUp}
        </button>
        {showDropdown && (
          <div className="metric-table-group-selector" style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            zIndex: 1
          }}>
            {props.groups.map((group) => (
              <div key={group} style={{ padding: '8px 12px', cursor: 'pointer', textAlign: 'left' }} onClick={() => selectGroup(group)}>
                {group}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const getChartOptions = (darkMode) => {
    let xScalesConfig = {
      ticks: {
        maxTicksLimit: 5,
        maxRotation: 0,
        autoSkip: true,
        color: darkMode ? '#eae6e1' : '#434248',
        font: {
          size: 12, 
          family: '"DM Mono", arial, sans-serif',
        },
      },
      grid: {
        display: true,
      },
    }

    let yScalesConfig = {
      beginAtZero: props.metric == "stacked",
      stacked: props.metric == "stacked",
      ticks: {
        color: darkMode ? '#eae6e1' : '#434248',
        font: {
          size: 12, 
          family: '"DM Mono", arial, sans-serif',
        },
        callback: function(value, index, values) {
          return ` ${formatMetric(props.metric, value, "tick")}`;
        },
      },
      grid: {
        drawBorder: false,
      },
    }
    if (props.metric == "stacked") {
      yScalesConfig["min"] = 0;
      yScalesConfig["max"] = 100;
      yScalesConfig["ticks"]["stepSize"] = 20;
    }

    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        legend: {
          position: 'bottom',
          labels: { 
            padding: 20,
            color: darkMode ? '#eae6e1' : '#434248',
            font: {
              size: 16, 
              family: '"DM Mono", arial, sans-serif',
            },
            generateLabels: (chart) => {
              const datasets = chart.data.datasets;
              return datasets.map((dataset, i) => ({
                text: dataset.label,
                fillStyle: getLineColor(i, dataset.label, darkMode, props),
                strokeStyle: dataset.borderColor,
                lineWidth: 2,
                hidden: !chart.isDatasetVisible(i),
                datasetIndex: i,
                fontColor: darkMode ? '#eae6e1' : '#434248',
              }));
            },
          },
          reverse: (spendFunnelAllocationChart || prospectingVsRemarketingChart || brandedVsNonBrandedChart) ? true : false,
        },
        title: {
          display: false,
        },
        tooltip: {
          titleFont: {
            size: 16,
            family: '"DM Mono", arial, sans-serif',
          },
          bodyFont: {
            size: 16,
            family: '"DM Mono", arial, sans-serif',
          },
          callbacks: {
            label: function(context) {
              let label = context.dataset.label || '';
    
              if (label) {
                label += ': ';
              }

              if (props.metric == "stacked" && props.tooltipData) {
                // Show the actual metric which is passed in through props.tooltipData
                label += formatMetric(props.tooltipMetric, props.tooltipData[context.dataset.label][context.dataIndex], "");
              } else if (context.parsed.y !== null) {
                label += formatMetric(props.metric, context.parsed.y, "");
              }
              return label;
            },
          },
          itemSort: function (a, b) {
            // Check if the sorting should be reversed
            if (spendFunnelAllocationChart || prospectingVsRemarketingChart || brandedVsNonBrandedChart) {
              return b.datasetIndex - a.datasetIndex;
            }
            // Otherwise, keep the default sorting (ascending)
            return a.datasetIndex - b.datasetIndex;
          }
        },
      },
      scales: {
        x: xScalesConfig,
        y: yScalesConfig,
      },
      elements: {
        line: {
          cubicInterpolationMode: 'monotone',
          tension: 0.4,
        },
      },
    }
  };
  
  const labels = props.labels;
  let orderedDatasetNames = props.datasetNames;
  if (spendFunnelAllocationChart) { // Stacked charts draw from bottom to top so we have to reverse the order so that Top of funnel appears on top
    const order = ["Uncategorized", "Lower Funnel", "Mid Funnel", "Top of Funnel"]
    orderedDatasetNames = order.filter(name => props.datasetNames.includes(name));
  }
  if (prospectingVsRemarketingChart) { // Stacked charts draw from bottom to top so we have to reverse the order so that Prospecting appears on top
    const order = ["Uncategorized", "Rem. Spend", "Pros. Spend"]
    orderedDatasetNames = order.filter(name => props.datasetNames.includes(name));
  }
  if (brandedVsNonBrandedChart) { // Stacked charts draw from bottom to top so we have to reverse the order so that Branded appears on top
    const order = ["Non-Branded", "Branded"]
    orderedDatasetNames = order.filter(name => props.datasetNames.includes(name));
  }
  let data = {
    labels,
    datasets: orderedDatasetNames.map((name, index) => {
      let fillStyle = false;
      if (props.metric == "stacked") {
        fillStyle = index == 0 ? "origin" : "-1";
      }
      let trendlineLinear = null;
      const currentGroupData = selectedGroup ? labels.map((day, index) => jsDig([toLower(selectedGroup), name, index], props.data)) : labels.map((day, index) => jsDig([name, index], props.data));
      if (currentGroupData.filter(x => x).length > 1 && props.metric != "stacked") { // Only show trendline if there are more than 1 data points and not stacked
        trendlineLinear = {
          lineStyle: "dotted",
          width: 2,
          color: getLineColor(index, name, darkMode, props),
        }
      }
      const urlParams = new URLSearchParams(window.location.search);
      const selectedYear = urlParams.get("year") || new Date().getFullYear().toString(); // Default to the current year
      const thisYear = parseInt(selectedYear);
      const lastYear = thisYear - 1;

      return ({
        label: name,
        fill: fillStyle,
        pointRadius: props.metric == "stacked" ? 0 : 0,
        pointHoverRadius: 3,
        data: currentGroupData,
        borderColor: getLineColor(index, name, darkMode, props),
        backgroundColor: props.metric == "stacked" ? hexToRGBA(getLineColor(index, name, darkMode, props), 0.5) : getLineColor(index, name, darkMode, props),
        borderJoinStyle: "round",
        borderWidth: name == lastYear ? 2 : 3,
        trendlineLinear: trendlineLinear,
      })
    }),
  };

  let chartHelpModal = null;
  if (props.chartName == "Branded vs. Non-Branded L60D") {
    chartHelpModal = <ChartHelpModal title="Branded vs. Non-Branded L60D" />;
  } else if (props.chartName == "Marketing Spend Funnel Allocation") {
    chartHelpModal = <ChartHelpModal title="Marketing Spend Funnel Allocation" />;
  } else if (props.chartName == "Prospecting vs. Remarketing L60D") {
    chartHelpModal = <ChartHelpModal title="Prospecting vs Remarketing L60D" />;
  }

  const handleCollapse = (e) => { 
    e.preventDefault();
    e.stopPropagation();
    const target = e.target;
    if (target.closest(".chart-help-modal") || target.classList.contains("chart-help-icon") || target.closest(".lite-group-selector")) {
      return; // Do not collapse if the click happened inside the help modal, the help icon, or the dropdown toggle
    }
    if (collapsible) {
      toggleCollapse();
    }
  }

  return (
    <>
      <div className="lite-metric-chart-container" style={props.disabledMsg ? {opacity: "0.5"} : null}>
        <div className={`lite-metric-chart-header ${collapsible ? "collapsible" : ""}`} onClick={(e) => handleCollapse(e)}>
          <div className="lite-group-selector">
            {renderGroupSelector()}
          </div>
          <h3>{props.chartName}</h3>
          <div className="lite-metric-chart-icons">
            {chartHelpModal}
            {collapsible && (
              <div className="collapse-toggle">
                {isCollapsed ? chevronDown : chevronUp}
              </div>
            )}
          </div>
        </div>
        <div 
          ref={chartContainerRef}
          className="lite-metric-chart-inner"
          style={{
            height: props.fixedHeight ? `${props.fixedHeight}px` : undefined,
            transition: 'max-height 0.5s ease',
            maxHeight: isCollapsed ? '0px' : '1700px'
          }}
        >
            <Line options={getChartOptions(darkMode)} data={data}  />
        </div>
        {props.disabledMsg && (
          <div className="coming-soon-container">
            {props.disabledMsg}
          </div>
        )}
      </div>
    </>
  )
}

function hexToRGBA(hex, alpha) {
  let r = parseInt(hex.slice(1, 3), 16),
      g = parseInt(hex.slice(3, 5), 16),
      b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function getLineColor(index, label, darkMode, props) {
  // TODO: Colors aren't finalized yet but we can modify these to make it easier to understand which traffic is from which brand
  const newLabel = String(label).toLowerCase();
  
  const urlParams = new URLSearchParams(window.location.search);
  const selectedYear = urlParams.get("year") || new Date().getFullYear().toString(); // Default to the current year
  const thisYear = parseInt(selectedYear);
  const lastYear = thisYear - 1;

  if (String(thisYear) == newLabel) {
    if (props.colorBlindAssist == true) {
      return "#0072B2";
    }
    else {
      return "#1E9C77";
    }
  }
  if (String(lastYear) == newLabel) {
    if (props.colorBlindAssist == true) {
      return "#E69F00";
    }
    else {
      return hexToRGBA("#7E7D7E", 0.4)
    }
  }
  if (newLabel == "top of funnel") {
    return "#1E9C77";
  }
  if (newLabel == "mid funnel") {
    return "#2488BB";
  }
  if (newLabel == "lower funnel") {
    return "#BF9E40";
  }
  if (newLabel == "pros. spend") {
    return "#1E9C77";
  }
  if (newLabel == "rem. spend") {
    return "#2488BB";
  }
  if (newLabel == "other" || newLabel == "uncategorized") {
    return "#7E7D7E";
  }
  if (newLabel == "snapchat") {
    return "#BF9E40";
  }
  if (newLabel == "facebook") {
    return "#2488BB";
  }
  if (newLabel == "instagram") {
    return "#EE884F";
  }
  if (newLabel == "youtube") {
    return "#C75B5B";
  }
  if (newLabel == "twitter") {
    return "#22BEBE";
  }
  if (newLabel == "tiktok") {
    return "#CB55B1";
  }
  if (newLabel == "linkedin") {
    return "#1E9C77";
  }
  
  const lightColors = ["#1E9C77", "#2488BB", "#BF9E40", "#6746EA", "#22BEBE", "#CB55B1", "#FFD23F", "#7E7D7E", "#C75B5B"];;
  const darkColors = ["#1E9C77", "#2488BB", "#BF9E40", "#6746EA", "#22BEBE", "#CB55B1", "#FFD23F", "#7E7D7E", "#C75B5B"];;
  return darkMode ? darkColors[index % darkColors.length] : lightColors[index % lightColors.length];
}